import React from "react";
import { Box, Stack, Typography, Skeleton } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useTheme } from "@mui/material/styles";
import TypingEffect from "../../components/patterns/typoraphy/TypingEffect";
import LoopVideoBlock from "../../components/video/LoopVideoBlock";
import { useIsMobile } from "../../hooks/useIsMobile";

/**
 * MagazineTopSection
 *
 * Props:
 * @param {string} title - 이슈 타이틀 [Required]
 * @param {string} description - 이슈 설명 [Optional]
 * @param {string} [dateText] - 상단 표시용 날짜 텍스트(예: '2025년 8월호') [Optional]
 * @param {boolean} isMobile - 모바일 여부 [Optional, 기본값: false]
 * @param {object} [videoCacheInfo] - 비디오 캐시 정보 객체 [Optional]
 * @param {boolean} [isLoading] - 로딩 상태 [Optional, 기본값: false]
 *
 * Example usage:
 * <MagazineTopSection title={issue.title} description={issue.description} isMobile={isMobile} videoCacheInfo={videoCacheInfo} />
 */
const MagazineTopSection = ({
	title,
	description,
	dateText,
	isMobile = false,
	videoCacheInfo,
	isLoading = false,
}) => {
	const theme = useTheme();
	const isMD = useIsMobile("md");

	// 비디오 URL 결정 (캐시 또는 직접 URL)
	const videoUrl =
		videoCacheInfo?.url ||
		`https://firebasestorage.googleapis.com/v0/b/my-threads-insight.firebasestorage.app/o/videos%2Fmonthly-cover-${title?.match(/\d{4}-\d{2}/)?.[0] || "2025-08"
		}.mp4?alt=media`;

	return (
		<Stack
			spacing={isMobile ? 2 : 4}
			sx={{
				width: "100%",
				px: isMobile ? 0 : 0,
			}}
		>
			<Stack spacing={1}>
				{isLoading ? (
					<Skeleton
						variant="text"
						width="60%"
						height={isMobile ? 60 : 100}
						sx={{
							fontSize: isMobile ? "2rem" : "3.5rem",
							backgroundColor: theme.palette.action.hover,
						}}
					/>
				) : (
					<TypingEffect
						texts={[title]}
						variant={{ xs: "h3", sm: "h3", md: "h2", lg: "h2" }}
						textColor={theme.palette.primary.main}
						fontFamily={
							'"source-han-serif-korean", "Noto Serif KR", "Noto Serif CJK KR", "Source Han Serif KR", "Apple SD Gothic Neo", "Malgun Gothic", "맑은 고딕", serif'
						}
						fontWeight={800}
						textAlign="left"
						cursorType="circle"
						cursorColor={theme.palette.primary.main}
						typingSpeed={60}
						deleteSpeed={30}
						sx={{ ml: -0.5 }}
						respectInView={false}
						persistKey={`magazine-top:${title || ''}`}
						loop={false}
					/>
				)}
			</Stack>
			<Grid
				container
				spacing={isMobile ? 0 : 16}
				alignItems={"stretch"}
				direction={isMD ? "column-reverse" : "row"}
			>
				<Grid size={{ xs: 12, md: 5 }} sx={{ display: "flex", height: "100%" }}>
					<Stack
						spacing={isMobile ? 2 : 6}
						sx={{
							width: "100%",
							alignItems: "flex-start",
							justifyContent: "flex-end",
							flex: 1,
							minHeight: "100%",
							px: isMobile ? 2 : 0,
						}}
					>
						<Stack
							spacing={isMobile ? 0.5 : 1}
							sx={{ width: "100%", alignItems: "flex-start" }}
						></Stack>
						{isLoading ? (
							<Stack spacing={1} sx={{ width: "100%" }}>
								<Skeleton
									variant="text"
									width="90%"
									height={isMobile ? 24 : 32}
								/>
								<Skeleton
									variant="text"
									width="85%"
									height={isMobile ? 24 : 32}
								/>
								<Skeleton
									variant="text"
									width="70%"
									height={isMobile ? 24 : 32}
								/>
							</Stack>
						) : description ? (
							<Box sx={{ width: "100%" }}>
								{typeof description === "string" ? (
									description.split(/\n+/).map((paragraph, pIdx) => {
										if (!paragraph.trim()) return null;

										return (
											<Typography
												key={pIdx}
												variant={isMobile ? "body1" : "h6"}
												sx={{
													fontWeight: 300,
													textAlign: "left",
													color: theme.palette.text.primary,
													position: "relative",
													mb: isMobile ? 2 : 8,
													"&::first-letter": {
														float: "left",
														fontSize: isMobile ? "2em" : "2.5em",
														lineHeight: "1.2",
														fontWeight: 900,
														marginRight: "0.08em",
														marginTop: "0.05em",
														fontFamily:
															'"source-han-serif-korean", "Noto Serif KR", serif',
														color: theme.palette.primary.main,
													},
												}}
											>
												{paragraph.split(/(".*?"|'.*?')/g).map((part, idx) => {
													if (!part) return null;
													if (part.startsWith('"') && part.endsWith('"')) {
														return (
															<Box
																key={idx}
																component="span"
																sx={{
																	display: "inline-block",
																	backgroundColor: theme.palette.text.primary,
																	color: theme.palette.background.paper,
																	px: 0.5,
																	py: 0.1,
																	borderRadius: 0.5,
																	mx: 0.2,
																}}
															>
																{part}
															</Box>
														);
													}
													if (part.startsWith("'") && part.endsWith("'")) {
														return (
															<Box
																key={idx}
																component="span"
																sx={{
																	fontWeight: 700,
																	mx: 0.1,
																	display: "inline",
																}}
															>
																{part}
															</Box>
														);
													}
													return <span key={idx}>{part}</span>;
												})}
											</Typography>
										);
									})
								) : (
									<Typography
										variant={isMobile ? "body1" : "h6"}
										sx={{
											fontWeight: 300,
											textAlign: "left",
											color: theme.palette.text.primary,
										}}
									>
										{description}
									</Typography>
								)}
							</Box>
						) : null}
					</Stack>
				</Grid>
				<Grid size={{ xs: 12, md: 7 }} sx={{ display: "flex" }}>
					<Box
						sx={{
							width: "100%",
							height: "100%",
							display: "flex",
							alignItems: "flex-end",
							justifyContent: isMobile ? "center" : "flex-end",
							px: isMobile ? 2 : 0,
						}}
					>
						{isLoading ? (
							<Skeleton
								variant="rectangular"
								width={isMobile ? "100%" : "75%"}
								height={isMobile ? 300 : 400}
								sx={{
									borderRadius: 2,
									alignSelf: "center",
									mb: isMobile ? 2 : "-128px",
									backgroundColor: theme.palette.action.hover,
								}}
							/>
						) : (
							<LoopVideoBlock
								videoUrl={videoUrl}
								aspectRatio="1 / 1"
								width={isMobile ? "100%" : "75%"}
								borderRadius={2}
								isMobile={isMobile}
								playOnHover={false}
								containerSx={{
									alignSelf: "center",
									mb: isMobile ? 2 : "-128px",
								}}
							/>
						)}
					</Box>
				</Grid>
			</Grid>
		</Stack>
	);
};

export default MagazineTopSection;
